import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { HardHat, Loader2, Copy, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { formatApiError } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetUrl, setResetUrl] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      if (res.data.reset_url) {
        setResetUrl(`${window.location.origin}${res.data.reset_url}`);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(resetUrl);
    toast({ title: 'Link copied' });
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10 bg-[#0a0a0c] grid-bg">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-9 h-9 rounded-lg bg-[#ea9a6f] flex items-center justify-center">
            <HardHat className="w-5 h-5 text-[#0a0a0c]" strokeWidth={2.5} />
          </div>
          <span className="serif text-3xl text-white">Crew<span className="text-[#ea9a6f]">Face</span></span>
        </Link>

        <div className="bg-[#101014] border border-[#1e1e26] rounded-2xl p-8">
          <h1 className="serif text-3xl text-white mb-1">Reset your password</h1>
          <p className="text-sm text-[#8a877f] mb-6">Enter your work email and we'll generate a reset link.</p>

          {!submitted ? (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-[#c9c6bf] text-sm">Work email</Label>
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="sam@company.com" data-testid="forgot-email-input" className="mt-1.5 bg-[#0a0a0c] border-[#26262e] text-white h-11" />
              </div>
              {error && <p className="text-sm text-red-400" data-testid="forgot-error-message">{error}</p>}
              <Button type="submit" disabled={loading} data-testid="forgot-submit-button" className="w-full bg-[#ea9a6f] hover:bg-[#e0895a] text-[#0a0a0c] font-medium rounded-full h-11">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : 'Generate reset link'}
              </Button>
            </form>
          ) : (
            <div className="space-y-3" data-testid="forgot-success-panel">
              {resetUrl ? (
                <>
                  <p className="text-sm text-[#c9c6bf]">No email delivery is set up yet — here's your reset link:</p>
                  <div className="bg-[#0a0a0c] border border-[#1e1e26] rounded-lg p-3 text-sm text-[#7dd4c5] break-all" data-testid="reset-link-display">{resetUrl}</div>
                  <div className="flex gap-2">
                    <Button onClick={copyLink} data-testid="copy-reset-link-button" className="flex-1 bg-[#7dd4c5] hover:bg-[#6bc4b5] text-[#0a0a0c] rounded-full h-10"><Copy className="w-4 h-4 mr-2" /> Copy link</Button>
                    <Link to={resetUrl.replace(window.location.origin, '')} className="flex-1">
                      <Button variant="outline" data-testid="open-reset-link-button" className="w-full border-[#2a2a30] bg-transparent text-white hover:bg-[#14141a] rounded-full h-10">Open it now</Button>
                    </Link>
                  </div>
                </>
              ) : (
                <p className="text-sm text-[#c9c6bf] flex items-center gap-2"><Check className="w-4 h-4 text-[#7dd4c5]" /> If an account exists for this email, a reset link has been generated.</p>
              )}
            </div>
          )}

          <div className="dashed-divider my-6" />
          <p className="text-sm text-[#8a877f] text-center"><Link to="/sign-in" className="text-[#ea9a6f] hover:underline">Back to sign in</Link></p>
        </div>
      </div>
    </div>
  );
}
